import React from "react";
import { Box, Typography, Button } from "@mui/material";
import { AccessTime } from "@mui/icons-material";
import { MessageBubbleContainer, MessageContent } from "./SharedStyledComponents";
import WalletPaymentDialog from "../WalletPaymentDialog";

const PaymentRequestIndividualRemainingAmountMessage = ({
  message,
  isOwn,
  currentUser,
  storedUserId,
  onOpenPaymentDialog,
  getReadReceiptStatus,
  getCurrencySymbol,
  formattedMessage,
  wrappedOnOpenPaymentDialog,
  walletPaymentDialogOpen,
  handleCloseWalletPaymentDialog,
  handleSendManualPaymentRequest,
}) => {
  const currentUserId = currentUser?.userId ?? storedUserId;

  const isUserInInitiationFor =
    message.initiationFor && message.initiationFor.includes(currentUserId);

  if (!isUserInInitiationFor) {
    return null;
  }

  const orderData = message?.orderId;
  const orderBusinessId = orderData?.businessId?._id || orderData?.businessId;
  const orderLocationId = orderData?.locationId?._id || orderData?.locationId;

  let isCurrentUserOwnerOrTeamMemberForThisOrder = false;
  const employeeInfo = currentUser?.employee;
  if (employeeInfo) {
    const employeeBusinessId =
      employeeInfo?.businessId?._id || employeeInfo?.businessId;
    const isSameBusiness =
      employeeBusinessId &&
      orderBusinessId &&
      employeeBusinessId === orderBusinessId;

    let hasSameLocation = false;
    if (employeeInfo?.locations && Array.isArray(employeeInfo.locations)) {
      hasSameLocation = employeeInfo.locations.some((loc) => {
        const locId = loc?._id || loc;
        return orderLocationId && locId === orderLocationId;
      });
    }

    isCurrentUserOwnerOrTeamMemberForThisOrder = Boolean(
      isSameBusiness || hasSameLocation
    );
  }

  if (isCurrentUserOwnerOrTeamMemberForThisOrder && !isUserInInitiationFor) {
    return null;
  }

  const remainingAmount = message?.amount !== undefined ? message.amount : (orderData?.remainingAmount || 0);
  const currencyCode = orderData?.currency || "USD";
  const currencySymbol = getCurrencySymbol(currencyCode);
  const isPaymentCompleted = orderData?.paymentCompleted === true;
  const customMessageText = formattedMessage.text;

  return (
    <>
      <MessageBubbleContainer isOwn={isOwn}>
        {!isOwn && (
          <Typography
            variant="caption"
            sx={{
              color: "#374151",
              fontSize: { xs: "0.75rem", sm: "0.8rem" },
              fontWeight: 600,
              mb: 0.5,
              alignSelf: "flex-start",
            }}
          >
            {message.senderId?.fullName || message.senderId?.userName || "user"}
          </Typography>
        )}

        <MessageContent
          isOwn={isOwn}
          isTemp={message.isTemp}
          isOrder={false}
          isPayment={true}
          isRequest={false}
          idDraft={false}
          isDisabled={false}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 2,
              width: "100%",
              minWidth: "280px",
              maxWidth: "380px",
              cursor: "pointer",
              "&:hover": {
                opacity: 0.9,
              },
            }}
          >
            <Box
              sx={{
                color: isOwn ? "#FFFFFF" : "#000000",
                fontSize: "16px",
                fontWeight: 600,
                lineHeight: 1.3,
                textAlign: "left",
              }}
            >
              {customMessageText || `Remaining amount: ${currencySymbol}${remainingAmount.toFixed(2)}`}
            </Box>

            {remainingAmount > 0 && !isPaymentCompleted && message?.buttonDisabled !== true && !isCurrentUserOwnerOrTeamMemberForThisOrder && onOpenPaymentDialog && (!message.messageFor || message.messageFor === currentUserId) && (
              <Box sx={{ display: "flex", justifyContent: "center", mt: 1 }}>
                <Button
                  variant="contained"
                  onClick={(e) => {
                    e.stopPropagation();
                    wrappedOnOpenPaymentDialog(message);
                  }}
                  sx={{
                    backgroundColor: isOwn ? "#ffffff" : "#7C3AED",
                    color: isOwn ? "#7C3AED" : "#ffffff",
                    borderRadius: 2,
                    px: 3,
                    py: 1,
                    fontSize: "14px",
                    fontWeight: 600,
                    textTransform: "none",
                    "&:hover": {
                      backgroundColor: isOwn ? "#f3f4f6" : "#6d28d9",
                    },
                  }}
                >
                  pay {currencySymbol}{remainingAmount.toFixed(2)}
                </Button>
              </Box>
            )}
          </Box>
        </MessageContent>

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: isOwn ? "flex-end" : "flex-start",
            gap: 1,
            mt: 1,
            width: "100%",
          }}
        >
          <Typography
            variant="caption"
            sx={{
              color: "rgba(0,0,0,0.6)",
              fontSize: { xs: "0.7rem", sm: "0.75rem" },
            }}
          >
            {message.timestamp}
          </Typography>

          {isOwn && (
            <Typography
              variant="caption"
              sx={{
                color: "rgba(0,0,0,0.6)",
                fontSize: { xs: "0.7rem", sm: "0.75rem" },
              }}
            >
              {message.isTemp ? (
                <AccessTime fontSize="inherit" />
              ) : (
                getReadReceiptStatus() || "✓"
              )}
            </Typography>
          )}
        </Box>
      </MessageBubbleContainer>

      <WalletPaymentDialog
        open={walletPaymentDialogOpen}
        onClose={handleCloseWalletPaymentDialog}
        onSendManualPayment={handleSendManualPaymentRequest}
      />
    </>
  );
};

export default PaymentRequestIndividualRemainingAmountMessage;
